"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useActiveAlerts = exports.sensorAlertToCardData = exports.formatAlertSensorLabel = void 0;
const react_1 = require("react");
const useAlertActions_1 = require("./useAlertActions");
const useAlertChangeRefresh_1 = require("./useAlertChangeRefresh");
const queries_1 = require("../../graphql/queries");
const types_1 = require("../../types");
const alertStatus_1 = require("../../utils/alertStatus");
const graphqlCache_1 = require("../../utils/graphqlCache");
const stableValue_1 = require("../../utils/stableValue");
const useStableList_1 = require("../useStableList");
const POLLING_INTERVAL_MS = 30_000;
/**
 * The air handler belongs to `hvac` ONLY.
 *
 * It used to be listed under `air` as well, back when filter health rendered on
 * the Air Quality screen. Filter health moved to the HVAC card, so the reason is
 * gone — but the dual listing outlived it and put "Filter Change Overdue" and
 * "HVAC Current Sensor Fault" on the Air Quality tab, where a homeowner reading
 * an IAQ screen has no reason to look for an equipment fault and an installer
 * looking at HVAC could reasonably assume it was already covered elsewhere.
 *
 * Equipment-specific alerts go to the section that owns the equipment. `air` is
 * air-quality monitors; the air handler's faults are HVAC's.
 */
const CATEGORY_SENSOR_TYPES = {
    water: [types_1.SensorType.WATER_QUALITY, types_1.SensorType.LEAK_DETECTOR],
    hvac: [types_1.SensorType.HVAC_CONDENSER_MONITOR, types_1.SensorType.HVAC_AIRHANDLER_MONITOR],
    air: [types_1.SensorType.AIR_QUALITY],
    electrical: [types_1.SensorType.POWER, types_1.SensorType.POWER_ALT],
};
function getAlertActions(alertType, sensorType) {
    const requestService = {
        label: 'Request service',
        variant: 'primary',
        actionId: 'requestService',
    };
    const clearAlert = {
        label: 'Clear alert',
        variant: 'secondary',
        actionId: 'clearAlert',
    };
    const orderFilter = {
        label: 'Order filter',
        variant: 'tertiary',
        actionId: 'orderFilter',
    };
    // Resolves the alert the way the homeowner actually resolves it: flags a
    // fresh baseline capture and acknowledges the row. It takes `clearAlert`'s
    // slot on filter cards instead of adding a fourth button — the hub's alert
    // card is height-boxed by the 1920x480 display, and a plain dismiss here is
    // near-useless (the hourly Lambda re-mints the alert while health < 60%).
    const resetFilterBaseline = {
        label: 'I replaced it',
        variant: 'secondary',
        actionId: 'resetFilterBaseline',
    };
    switch (alertType) {
        case types_1.SensorAlertType.HVACfilter:
        case types_1.SensorAlertType.HvacFilter:
            return [requestService, orderFilter, resetFilterBaseline];
        // Offline alerts auto-resolve when data returns. "Request service" is
        // offered when the sensor type has a real service flow to route to
        // (water / power / HVAC / air quality) — useAlertActions routes it by
        // sensorType.
        case types_1.SensorAlertType.SensorInactive:
        case types_1.SensorAlertType.AqSensorInactive:
        case types_1.SensorAlertType.HubInactive:
        case types_1.SensorAlertType.HubOffline:
        case types_1.SensorAlertType.HubRadioFault:
            return (0, useAlertActions_1.canRequestOfflineService)(sensorType)
                ? [requestService, { ...clearAlert, label: 'Dismiss' }]
                : [{ ...clearAlert, label: 'Dismiss' }];
        default:
            return [requestService, clearAlert];
    }
}
/**
 * Maps a raw `SensorAlert` row onto the `AlertCardData` shape `AlertWidgetCard`
 * renders. Exported so surfaces that fetch alerts themselves (the web-hub
 * Alerts screen via `useHubAlertFeed`) reuse the same title / severity /
 * action mapping instead of duplicating it.
 */
/**
 * "Location · sensor name" label — homeowners asked for the sensor's actual
 * name alongside the room it's in. The name is skipped when it just repeats
 * the location.
 */
function formatAlertSensorLabel(sensor) {
    if (!sensor)
        return 'Unknown Sensor';
    const place = [sensor.location, sensor.sublocation].filter(Boolean).join(' — ');
    if (!place)
        return sensor.name || 'Unknown Sensor';
    if (sensor.name && sensor.name !== sensor.location)
        return `${place} · ${sensor.name}`;
    return place;
}
exports.formatAlertSensorLabel = formatAlertSensorLabel;
function sensorAlertToCardData(alert, sensor) {
    const location = formatAlertSensorLabel(sensor);
    return {
        id: alert.id,
        // Type-aware: a sensor's own offline row is coverage-yellow, not hazard-orange.
        severity: (0, alertStatus_1.alertCardSeverity)(alert),
        title: (0, alertStatus_1.getAlertTitle)(alert.alertType, (0, alertStatus_1.alertVariant)(alert)),
        message: alert.message || `Alert on ${location}`,
        location,
        sensorId: alert.sensorId,
        // Sensor first: the alert row's hubId is the hub that RAISED the alert,
        // which for a cloud-side sweep is the same thing, but the sensor's own
        // hubId is the authoritative answer to "whose Z-Wave network is this on?".
        hubId: sensor?.hubId ?? alert.hubId,
        sensorType: sensor?.type,
        alertType: alert.alertType,
        actions: getAlertActions(alert.alertType, sensor?.type),
        startedAt: alert.firstTriggeredAt ?? alert.triggeredAt ?? alert.timestamp,
    };
}
exports.sensorAlertToCardData = sensorAlertToCardData;
const useActiveAlerts = (hubId, category, allSensors) => {
    const sensors = allSensors ?? [];
    const [rawAlerts, setRawAlerts] = (0, react_1.useState)([]);
    const [loading, setLoading] = (0, react_1.useState)(true);
    const intervalRef = (0, react_1.useRef)(null);
    const lastFetchTime = (0, react_1.useRef)(0);
    // No hubId filter — see useWaterAggregation: `sensors` is caller-scoped,
    // and on the hub display that scope includes linked gateways. Filtering on
    // `hubId` here meant gateway sensors never badged an alert.
    const categorySensors = (0, useStableList_1.useStableList)(hubId && category
        ? sensors.filter((s) => CATEGORY_SENSOR_TYPES[category].includes(s.type))
        : []);
    const fetchAlerts = (0, react_1.useCallback)(async (force = false) => {
        if (categorySensors.length === 0) {
            setRawAlerts([]);
            setLoading(false);
            return;
        }
        const now = Date.now();
        if (!force && now - lastFetchTime.current < 5000)
            return;
        lastFetchTime.current = now;
        if (force)
            (0, graphqlCache_1.clearGraphqlCache)();
        try {
            const results = await Promise.all(categorySensors.map(async (sensor) => {
                try {
                    const response = await (0, graphqlCache_1.cachedGraphql)({
                        query: queries_1.getSensorAlertsBySensor,
                        variables: { sensorId: sensor.id, limit: 20 },
                    });
                    return response.data?.getSensorAlertsBySensor || [];
                }
                catch {
                    return [];
                }
            }));
            const allAlerts = results.flat();
            // Filter to active, unacknowledged alerts
            const active = allAlerts.filter((a) => (0, types_1.isAlertActive)(a));
            // Deduplicate by ID
            const seen = new Set();
            const deduped = active.filter((a) => {
                if (seen.has(a.id))
                    return false;
                seen.add(a.id);
                return true;
            });
            setRawAlerts((prev) => (0, stableValue_1.stable)(prev, deduped));
        }
        catch {
            // Keep existing
        }
        finally {
            setLoading(false);
        }
    }, [categorySensors.map((s) => s.id).join(',')]);
    (0, react_1.useEffect)(() => {
        fetchAlerts();
        intervalRef.current = setInterval(() => fetchAlerts(), POLLING_INTERVAL_MS);
        return () => {
            if (intervalRef.current)
                clearInterval(intervalRef.current);
        };
    }, [fetchAlerts]);
    const refreshOnAlertChange = (0, react_1.useCallback)(() => {
        fetchAlerts(true);
    }, [fetchAlerts]);
    (0, useAlertChangeRefresh_1.useAlertChangeRefresh)(refreshOnAlertChange);
    const alertCards = (0, react_1.useMemo)(() => {
        const sensorMap = new Map(categorySensors.map((s) => [s.id, s]));
        return rawAlerts
            .map((alert) => sensorAlertToCardData(alert, sensorMap.get(alert.sensorId)))
            .sort((a, b) => {
            const severityOrder = {
                error: 0,
                warning: 1,
                coverage: 2,
                info: 3,
                success: 4,
            };
            return severityOrder[a.severity] - severityOrder[b.severity];
        });
    }, [rawAlerts, categorySensors]);
    return { alerts: alertCards, loading };
};
exports.useActiveAlerts = useActiveAlerts;
